import { inject, Injectable } from '@angular/core';
import { RideCargoExtraFields, RideJobExtraFields, RidesExtraFields } from '../../../shared/enums/extraFields';
import { DriverLookup } from '../../../shared/interfaces/driver-lookup.interface';
import { RideDocumentDto } from '../../../shared/interfaces/ride-firestore';
import { IContractorDialog, RideListItem } from '../../../shared/interfaces/ride-list-item';
import { RequestBuilderService } from '../../../shared/services/request-builder.service';
import { MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { ContractorDialogComponent } from '../components/contractor-dialog/contractor-dialog.component';
import { MapDialogComponent } from '../components/map-dialog/map-dialog.component';
import { AddIncidentReportDialogComponent } from '../components/ride-actions-dialogs/add-incident-report-dialog/add-incident-report-dialog.component';
import { AssignSubDriverDialogComponent } from '../components/ride-actions-dialogs/assign-sub-driver-dialog/assign-sub-driver-dialog.component';
import { ChangeRideTimeDialogComponent } from '../components/ride-actions-dialogs/change-ride-time-dialog/change-ride-time-dialog.component';
import { ConfirmDialogComponent } from '../components/ride-actions-dialogs/confirm-dialog/confirm-dialog.component';
import { DelayNotificationDialogComponent } from '../components/ride-actions-dialogs/delay-notification-dialog/delay-notification-dialog.component';
import { DeleteDialogComponent } from '../components/ride-actions-dialogs/delete-dialog/delete-dialog.component';
import { FindDriverDialogComponent } from '../components/ride-actions-dialogs/find-driver-dialog/find-driver-dialog.component';
import { PayoutAdjustmentDialogComponent } from '../components/ride-actions-dialogs/payout-adjustment-dialog/payout-adjustment-dialog.component';
import { RideActivityDialogComponent } from '../components/ride-actions-dialogs/ride-activity-dialog/ride-activity-dialog.component';
import { WaitingTimeDialogComponent } from '../components/ride-actions-dialogs/waiting-time-dialog/waiting-time-dialog.component';
import { RideBlueprintDialogComponent } from '../components/ride-blueprint-dialog/ride-blueprint-dialog.component';
import { MapRideServiceService } from './map-ride-service.service';
import { WaypointsActivityLogDialogComponent } from '../components/ride-actions-dialogs/waypoints-activity-log-dialog/waypoints-activity-log-dialog.component';
import { MessagingDialogComponent } from '../components/messaging-dialog/messaging-dialog.component';
import { Common } from '../../../shared/enums/common';
import { Subject } from 'rxjs';
import { ClientMonitorDialogComponent } from '../components/client-monitor-dialog/client-monitor-dialog.component';
import { PassegersEquipmentsDialogComponent } from '../components/ride-item/passegers-equipments-dialog/passegers-equipments-dialog.component';
import { ProviderAssignSubDriverDialogComponent } from '../components/ride-actions-dialogs/provider-assign-sub-driver-dialog/provider-assign-sub-driver-dialog.component';
import { CameraAuditLogComponent } from '../components/camera-audit-log/camera-audit-log.component';
import { CameraStatusService } from './camera-status.service';
import { CompleteRideDialogComponent } from '../components/ride-actions-dialogs/complete-ride-dialog/complete-ride-dialog.component';
import { StateService } from '../../../shared/services/state.service';

@Injectable({
  providedIn: 'root',
})
export class ActionDialogService {
  mapRideService = inject(MapRideServiceService);
  requestBuilder = inject(RequestBuilderService);
  stateService = inject(StateService);

  private readonly reFetchRidesSubject = new Subject<boolean>();
  reFetchRides$ = this.reFetchRidesSubject.asObservable();

  private readonly subdriverAssignedSubject = new Subject<boolean>();
  subdriverAssigned$ = this.subdriverAssignedSubject.asObservable();

  openMapDialog(data: {
    dialogService: DialogService;
    docRide: RideDocumentDto;
    mappedRide: RideListItem;
    rideId?: string; // Ride ID to subscribe to updates
    driverOnline: boolean;
    showRideTimeline?: boolean;
    emergencyDialog?: boolean;
    emergencyDetails?: string;
    emergencyDocId?: string;
  }) {
    let driverDetails = data.mappedRide.driverDetails;
    if (driverDetails) driverDetails.online = data.driverOnline;

    const ref = data.dialogService.open(MapDialogComponent, {
      showHeader: false,
      width: '900px',
      modal: true,
      styleClass: 'dispatch-portal-custom-dialog',
      breakpoints: {
        '960px': '640px',
        '640px': '400px',
        '575px': '90vw',
      },
      data: {
        id: data.mappedRide.id,
        showRideTimeline: data.showRideTimeline ?? true,
        emergencyDialog: data.emergencyDialog ?? false,
        emergencyDetails: data.emergencyDetails,
        emergencyDocId: data.emergencyDocId,
        rideTimeline: data.mappedRide.rideTimeline,
        rideDOT: data.mappedRide.rideInfo.strDateOfService,
        rideStatus: data.mappedRide.rideStatus,
        driverDetails,
        docRide: data.docRide,
        mappedRide: data.mappedRide,
        rideId: data.rideId, // Pass ride ID to dialog
      },
    })!;

    ref.onClose.subscribe(res => {
      ref.destroy();
    });
  }

  openBlueprintDialog(dialogService: DialogService, ride: RideListItem, docRide: RideDocumentDto) {
    if (docRide) {
      this.requestBuilder.get(`rides/blueprint-sheet/${docRide.ride_blueprint_uuid}`).subscribe(res => {
        const blueprintSheetData = res.data;

        dialogService.open(RideBlueprintDialogComponent, {
          showHeader: false,
          width: '900px',
          modal: true,
          styleClass: 'dispatch-portal-custom-dialog',
          breakpoints: {
            '992px': '640px',
            '768px': '90vw',
          },
          data: {
            blueprintSheetData,

            header: 'Ride Blueprint Sheet',
            id: ride.id,
            rideBlueprintId: ride.blueprintId,
            rideStops: ride.rideTimeline,
            rideStopsDoc: docRide.ride_stops,
            rideTimezone: ride.rideTime.utcTimezoneDiffBPFormat,
            rideType: ride.rideInfo.type,
            rideDuration: ride.rideInfo.duration,
            rideMilage: ride.rideInfo.milage,
            service: ride.driverDetails?.blueprintCarType,
            numberOfStudents: ride.rideInfo.numOfStudents,
            numberOfMonitors: ride.rideInfo.numOfMonitors,
            driverDetails: ride.driverDetails,
            ridePeriod: ride.rideInfo.period,
            rideDOT: ride.rideInfo.strDateOfService,
            contractorPayout: docRide.ride_extra_fields?.find(field => field.key_id == RidesExtraFields.ContractorOriginalPayout)?.value,
            driverPayout: docRide.ride_extra_fields?.find(field => field.key_id == RidesExtraFields.DriverPayout)?.value,
            payoutDetails: JSON.parse(docRide.ride_extra_fields?.find(field => field.key_id == RidesExtraFields.AssignmentDetails)?.value ?? ''),
          },
        });
      });
    }
  }

  openPassengersEquipmentsDialog(dialogService: DialogService, messageService: MessageService, docRide: RideDocumentDto) {
    dialogService.open(PassegersEquipmentsDialogComponent, {
      showHeader: false,
      width: '900px',
      modal: true,
      styleClass: 'dispatch-portal-custom-dialog',
      breakpoints: {
        '992px': '640px',
        '768px': '90vw',
      },
      data: {
        header: ``,
        docRide,
      },
    });
  }

  openContractorDialog(dialogService: DialogService, messageService: MessageService, ride: RideListItem, docRide: RideDocumentDto, isMonitor = false) {
    let mappedDriver: IContractorDialog | null = null;
    const driverDoc = docRide.ride_drivers?.find(b => !b.is_deleted);
    const monitorDoc = docRide.ride_cargoes?.find(b => !b.is_deleted && b.is_attendant);

    if (isMonitor && !monitorDoc?.is_partner) {
      this.requestBuilder.get(`rides/client-monitor/${monitorDoc?.assistant_id}`).subscribe(res => {
        console.log(res);

        const data = res.data;
        if (data) {
          dialogService.open(ClientMonitorDialogComponent, {
            showHeader: false,
            width: '900px',
            modal: true,
            styleClass: 'dispatch-portal-custom-dialog',
            breakpoints: {
              '992px': '640px',
              '768px': '90vw',
            },
            data: {
              header: ``,
              monitorData: data,
            },
          });
        } else {
          messageService.add({
            severity: 'danger',
            summary: 'Data Not Found!',
            detail: `No data found for this contractor.`,
            life: 6000,
          });
        }
      });
    } else {
      this.requestBuilder.get(`drivers/${isMonitor ? monitorDoc?.assistant_id ?? 0 : driverDoc?.driver_id ?? 0}`).subscribe(res => {
        console.log(res);

        const data = res.data as DriverLookup;
        if (data) {
          mappedDriver = this.mapRideService.getMappedContractor(data) || null;
          console.log(mappedDriver);

          dialogService.open(ContractorDialogComponent, {
            showHeader: false,
            width: '900px',
            modal: true,
            styleClass: 'dispatch-portal-custom-dialog',
            breakpoints: {
              '992px': '640px',
              '768px': '90vw',
            },
            data: {
              header: ``,
              id: ride.id,
              contractorData: mappedDriver,
              driverId: isMonitor ? ride.monitorDetails?.id : ride.driverDetails?.id,
              timezoneOffsetInSeconds: ride.timezoneOffsetInSeconds,
              driverName: isMonitor ? ride.monitorDetails?.name : ride.driverDetails?.name,
            },
          });
        } else {
          messageService.add({
            severity: 'danger',
            summary: 'Data Not Found!',
            detail: `No data found for this contractor.`,
            life: 6000,
          });
        }
      });
    }
  }

  openMessagingDialog(dialogService: DialogService, field: 'guardian' | 'contractor', allMessages: boolean, rideId?: string) {
    const dialogConfig = {
      showHeader: false,
      width: '1200px',
      modal: true,
      styleClass: 'dispatch-portal-custom-dialog messaging-dialog',
      contentStyle: { padding: '0 !important' },
      breakpoints: {
        '1200px': '800px',
        '960px': '640px',
        '768px': '400px',
        '575px': '90vw',
      },
      data: {
        field,
        allMessages,
        rideId,
      },
    };
    dialogService.open(MessagingDialogComponent, dialogConfig);
  }

  openWaypointsActivityLogDialog(dialogService: DialogService, originalRideId: number, stopId: any, rideTimeline: any[], timezoneOffsetInSeconds: number) {
    const dialogConfig = {
      showHeader: false,
      width: '1200px',
      modal: true,
      styleClass: 'dispatch-portal-custom-dialog',
      breakpoints: {
        '1200px': '900px',
        '960px': '640px',
        '768px': '90vw',
      },
      data: {},
    };

    dialogService.open(WaypointsActivityLogDialogComponent, {
      ...dialogConfig,
      data: {
        originalRideId: originalRideId,
        stopId: stopId,
        rideTimeline: rideTimeline,
        timezoneOffsetInSeconds: timezoneOffsetInSeconds,
      },
    });
  }

  openDialog(dialog: string, dialogService: DialogService, ride: RideListItem, docRide: RideDocumentDto, messageService: MessageService, onConfirmedMessage: string = '', selectedDriverId?: number) {
    const baseDialogConfig = this.createBaseDialogConfig(ride);

    const dialogHandlers = {
      rideActivity: () => this.openRideActivityDialog(dialogService, baseDialogConfig, ride),
      delayNotification: () => this.openDelayNotificationDialog(dialogService, baseDialogConfig, ride, messageService, onConfirmedMessage),
      assignSubDriver: () => this.openAssignSubDriverDialog(dialogService, baseDialogConfig, ride, docRide, messageService, onConfirmedMessage, selectedDriverId),
      providerAssignSubDriver: () => this.openProviderAssignSubDriverDialog(dialogService, baseDialogConfig, ride, docRide, messageService, onConfirmedMessage, selectedDriverId),
      addIncidentReport: () => this.openAddIncidentReportDialog(dialogService, baseDialogConfig, ride, docRide, messageService, onConfirmedMessage),
      findDriver: () => this.openFindDriverDialog(dialogService, baseDialogConfig, ride, docRide),
      waitingTime: () => this.openWaitingTimeDialog(dialogService, baseDialogConfig, ride, messageService, onConfirmedMessage),
      changeRideTime: () => this.openChangeRideTimeDialog(dialogService, baseDialogConfig, ride, messageService, onConfirmedMessage),
      payoutAdjustment: () => this.openPayoutAdjustmentDialog(dialogService, baseDialogConfig, messageService, onConfirmedMessage),
      completeRide: () => this.openCompleteRideDialog(dialogService, baseDialogConfig, ride, docRide, messageService, onConfirmedMessage),
    };

    const handler = dialogHandlers[dialog as keyof typeof dialogHandlers];
    if (handler) {
      handler();
    }
  }

  private createBaseDialogConfig(ride: RideListItem) {
    return {
      showHeader: false,
      width: '640px',
      modal: true,
      styleClass: 'dispatch-portal-custom-dialog',
      breakpoints: {
        '768px': '90vw',
      },
      data: {
        id: ride.id,
        uuid: ride.uuid,
      },
    };
  }

  private openRideActivityDialog(dialogService: DialogService, baseConfig: any, ride: RideListItem) {
    dialogService.open(RideActivityDialogComponent, {
      ...baseConfig,
      width: '800px',
      breakpoints: {
        '1200px': '640px',
        '768px': '90vw',
      },
      data: {
        dispatchId: ride.dispatchId,
        id: ride.id,
        rideTimeline: ride.rideTimeline,
        uuid: ride.uuid,
        timezoneOffsetInSeconds: ride.timezoneOffsetInSeconds,
      },
    });
  }

  private openDelayNotificationDialog(dialogService: DialogService, baseConfig: any, ride: RideListItem, messageService: MessageService, onConfirmedMessage: string) {
    const ref = dialogService.open(DelayNotificationDialogComponent, {
      ...baseConfig,
      data: {
        uuid: ride.uuid,
      },
    });
    this.showConfirmMessage(messageService, ref, onConfirmedMessage);
  }

  private createSubDriverData(ride: RideListItem, docRide: RideDocumentDto, selectedDriverId?: number) {
    const isSensitiveRide = docRide.ride_cargoes?.some(cargo => cargo.ride_cargo_extra_fields?.find(field => field.key_id == RideCargoExtraFields.IsSensitiveRide)?.value == 'true');

    return {
      id: ride.id,
      uuid: ride.uuid,
      blueprintId: ride.blueprintId,
      blueprintVehicleType: ride.driverDetails?.blueprintCarType,
      ridePassengerEquipments: docRide.ride_passenger_equipments?.map(e => ({
        id: e.equipment_id,
        name: e.equipment_name,
      })),
      isSensitiveRide,
      isCameraRequired: ride.isCameraRequired,
      rideMilage: ride.rideInfo.milage,
      rideDuration: ride.rideInfo.duration,
      oldDriverData: this.createOldDriverData(ride, docRide),
      blueprintDriverPayout: JSON.parse(docRide.ride_extra_fields?.find(field => field.key_id == RidesExtraFields.BlueprintDriverPayout)?.value ?? ''),
      state: docRide.ride_extra_fields?.find(field => field.key_id == RidesExtraFields.State)?.value,
      selectedDriverId,
    };
  }

  private createOldDriverData(ride: RideListItem, docRide: RideDocumentDto) {
    if (!ride.driverDetails || ride.driverDetails.id == Common.PartnerBotId) {
      return null;
    }

    const payoutAdjustment = Number(docRide.ride_jobs?.find(rj => !rj.is_deleted)?.ride_job_extra_fields?.find(f => f.key_id == RideJobExtraFields.PayoutAdjustment)?.value) || 0;

    return {
      driverName: ride.driverDetails?.name,
      driverId: ride.driverDetails?.id,
      driverProvider: ride.driverDetails.driverProvider,
      driverPayout: 0,
      driverProviderPayout: 0,
      payoutAdjustment,
    };
  }

  private openAssignSubDriverDialog(dialogService: DialogService, baseConfig: any, ride: RideListItem, docRide: RideDocumentDto, messageService: MessageService, onConfirmedMessage: string, selectedDriverId?: number) {
    const data = this.createSubDriverData(ride, docRide, selectedDriverId);
    const ref = dialogService.open(AssignSubDriverDialogComponent, {
      ...baseConfig,
      data,
    })!;

    ref.onClose.subscribe(res => {
      if (res?.confirmed) {
        this.subdriverAssignedSubject.next(true);
      }
    });
    this.showConfirmMessage(messageService, ref, onConfirmedMessage);
  }

  private openProviderAssignSubDriverDialog(dialogService: DialogService, baseConfig: any, ride: RideListItem, docRide: RideDocumentDto, messageService: MessageService, onConfirmedMessage: string, selectedDriverId?: number) {
    const data = this.createSubDriverData(ride, docRide, selectedDriverId);
    const ref = dialogService.open(ProviderAssignSubDriverDialogComponent, {
      ...baseConfig,
      data,
    })!;

    ref.onClose.subscribe(res => {
      if (res?.confirmed) {
        this.subdriverAssignedSubject.next(true);
      }
    });
    this.showConfirmMessage(messageService, ref, onConfirmedMessage);
  }

  private openAddIncidentReportDialog(dialogService: DialogService, baseConfig: any, ride: RideListItem, docRide: RideDocumentDto, messageService: MessageService, onConfirmedMessage: string) {
    const partnerName = ride.driverDetails?.number && ride.driverDetails?.number === String(Common.PartnerBotId) ? ride.driverDetails?.name.replace('Account', '').trim() : ride.driverDetails?.name?.split(' ')[0] ?? '';

    const ref = dialogService.open(AddIncidentReportDialogComponent, {
      ...baseConfig,
      data: {
        id: ride.id,
        uuid: ride.uuid,
        students: ride.students,
        rideDateOfService: docRide.date_of_service,
        partnerId: ride.driverDetails?.id,
        partnerName,
      },
    });
    this.showConfirmMessage(messageService, ref, onConfirmedMessage);
  }

  private openCompleteRideDialog(dialogService: DialogService, baseConfig: any, ride: RideListItem, docRide: RideDocumentDto, messageService: MessageService, onConfirmedMessage: string) {
    const ref = dialogService.open(CompleteRideDialogComponent, {
      ...baseConfig,
      data: {
        id: ride.id,
        uuid: ride.uuid,
        rideTimezone: ride.rideTime.utcTimezoneDiffBPFormat,
        ride,
      },
      width: '800px',
      breakpoints: {
        '992px': '640px',
        '768px': '90vw',
      },
    });
    this.showConfirmMessage(messageService, ref, onConfirmedMessage, true);
  }

  private openFindDriverDialog(dialogService: DialogService, baseConfig: any, ride: RideListItem, docRide: RideDocumentDto) {
    dialogService.open(FindDriverDialogComponent, {
      ...baseConfig,
      data: {
        id: ride.id,
        rideBlueprintId: ride.blueprintId,
        uuid: ride.uuid,
        vehicleType: ride.driverDetails?.blueprintCarType,
        rideEquipmentsIds: docRide.ride_passenger_equipments?.map(e => e.equipment_id),
        rideDateOfService: docRide.date_of_service,
        state: docRide.ride_extra_fields?.find(f => f.key_id == RidesExtraFields.State)?.value ?? '',
        rideMilage: ride.rideInfo.milage,
        rideDuration: ride.rideInfo.duration,
        rideLocationLong: JSON.parse(docRide.ride_extra_fields?.find(f => f.key_id == RidesExtraFields.LocationLongitude)?.value ?? ''),
        rideLocationLat: JSON.parse(docRide.ride_extra_fields?.find(f => f.key_id == RidesExtraFields.LocationLatitude)?.value ?? ''),
        blueprintDriverPayout: JSON.parse(docRide.ride_extra_fields?.find(field => field.key_id == RidesExtraFields.BlueprintDriverPayout)?.value ?? ''),
        ride,
        docRide,
      },
      width: '1200px',
      breakpoints: {
        '1200px': '800px',
        '992px': '640px',
        '768px': '90vw',
      },
    });
  }

  private openWaitingTimeDialog(dialogService: DialogService, baseConfig: any, ride: RideListItem, messageService: MessageService, onConfirmedMessage: string) {
    const ref = dialogService.open(WaitingTimeDialogComponent, {
      ...baseConfig,
      data: {
        id: ride.id,
        uuid: ride.uuid,
        isEdit: (ride.rideInfo.TotalBillableWaitingTimeInMinutes ?? 0) > 0,
      },
    });
    this.showConfirmMessage(messageService, ref, onConfirmedMessage);
  }

  private openChangeRideTimeDialog(dialogService: DialogService, baseConfig: any, ride: RideListItem, messageService: MessageService, onConfirmedMessage: string) {
    const ref = dialogService.open(ChangeRideTimeDialogComponent, {
      ...baseConfig,
      width: '800px',
      breakpoints: {
        '1200px': '640px',
        '768px': '90vw',
      },
      data: {
        id: ride.id,
        uuid: ride.uuid,
        rideStops: ride.rideTimeline,
        rideType: ride.rideInfo.type,
        rideTimezone: ride.rideTime.utcTimezoneDiff,
      },
    });
    this.showConfirmMessage(messageService, ref, onConfirmedMessage);
  }

  private openPayoutAdjustmentDialog(dialogService: DialogService, baseConfig: any, messageService: MessageService, onConfirmedMessage: string) {
    const ref = dialogService.open(PayoutAdjustmentDialogComponent, baseConfig);
    this.showConfirmMessage(messageService, ref, onConfirmedMessage);
  }

  openConfirmDialog(data: {
    dialogService: DialogService;
    action: string;
    header: string;
    messageService: MessageService;
    id: string;
    uuid: string;
    confirmationText: string;
    onConfirmedMessage: string;
    hasInput?: boolean;
    inputPlaceholder?: string;
    btnLabel?: string;
  }) {
    const ref = data.dialogService.open(ConfirmDialogComponent, {
      showHeader: false,
      width: '400px',
      modal: true,
      styleClass: 'dispatch-portal-custom-dialog',
      breakpoints: {
        '575px': '90vw',
      },
      data: {
        action: data.action,
        header: data.header,
        confirmationText: data.confirmationText,
        hasInput: data.hasInput,
        inputPlaceholder: data.inputPlaceholder,
        uuid: data.uuid,
        id: data.id,
        btnLabel: data.btnLabel,
      },
    })!;

    ref.onClose.subscribe(res => {
      if (res?.confirmed) {
        data.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: data.onConfirmedMessage,
          life: 6000,
        });
      }
    });
  }

  beingResetRideId: string | null = null;

  openDeleteDialog(data: { dialogService: DialogService; action: string; header: string; id: string; uuid: string; warningMessages: string[]; messageService: MessageService; onConfirmedMessage: string; inputPlaceholder: string }) {
    if (this.beingResetRideId == data.id) return;
    const ref = data.dialogService.open(DeleteDialogComponent, {
      showHeader: false,
      width: '640px',
      modal: true,
      styleClass: 'dispatch-portal-custom-dialog',
      breakpoints: {
        '768px': '90vw',
      },
      data: {
        action: data.action,
        header: data.header,
        id: data.id,
        warningMessages: data.warningMessages,
        inputPlaceholder: data.inputPlaceholder,
        uuid: data.uuid,
      },
    })!;

    ref.onClose.subscribe(res => {
      if (res?.confirmed) {
        data.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: data.onConfirmedMessage,
          life: 6000,
        });
      }
      if (res?.resetRide) {
        this.beingResetRideId = data.id;
        setTimeout(() => {
          this.beingResetRideId = null;
        }, 10000);
      }
    });
  }

  /**
   * Opens a camera dialog using the provided DialogService.
   *
   * @param cameraId - The unique identifier of the camera to display in the dialog.
   * @param dialogService - The DialogService instance used to open the dialog.
   *
   * @remarks
   * The dialog is configured with custom width, modal behavior, and responsive breakpoints.
   * The cameraId is passed as data to the dialog component.
   */
  openCameraDialog(partner_id: string | number, cameraOnlineStatus: boolean, lastSyncDate: Date | null, cameraStatusService: CameraStatusService, dialogService: DialogService, ride: RideListItem) {
    const dialogConfig = {
      showHeader: false,
      width: '1200px',
      modal: true,
      styleClass: 'dispatch-portal-custom-dialog',
      breakpoints: {
        '1200px': '900px',
        '960px': '640px',
        '768px': '90vw',
      },
      data: {},
    };
    dialogService.open(CameraAuditLogComponent, {
      ...dialogConfig,
      data: {
        partner_id: partner_id,
        rideId: ride.id,
        ride,
        cameraOnlineStatus,
        lastSyncDate,
        cameraStatusService: cameraStatusService,
      },
    });
  }

  private showConfirmMessage(messageService: MessageService, ref: any, message: string, isCompleteRide = false) {
    ref.onClose.subscribe((res: any) => {
      if (res?.confirmed) {
        if (isCompleteRide) {
          // Decrement pending rides count when a ride is completed
          const currentCount = this.stateService.getPendingRidesCount();
          if (currentCount > 0) {
            this.stateService.setPendingRidesCount(currentCount - 1);
          }
        }
        messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: message,
          life: 6000,
        });
      }
    });
  }
}
